<template>
  <div
    class="base-card"
    role="article"
    :aria-describedby="`content-${id}`"
    @click.stop.prevent="handleCardClick"
  >
    <div class="row-1">
      <div class="tags">
        <slot name="tags" />
        <a
          v-if="result.tagLink"
          :href="result.tagLink"
          class="tag"
          :class="result.tagLink ? 'tag-link' : ''"
          @click.stop
        >
          {{ result.tagName }}
        </a>
        <a
          v-if="result.orgLink"
          :href="result.orgLink || '/'"
          class="tag"
          :class="result.tagLink ? 'tag-link' : ''"
          @click.stop
        >
          {{ result.orgName }}
        </a>
      </div>
      <div class="likes-section">
        <slot name="likes-section" />
        <BaseCardLikeButtons
          :result="result"
          :is-content-loading="isContentLoading"
          @open-feedback-modal="openFeedbackModal"
        />
      </div>
    </div>
    <div class="row-2">
      <div
        v-if="$slots.preview"
        class="image-preview"
      >
        <slot name="preview" />
      </div>
      <div
        :id="`content-${id}`"
        class="content-section"
      >
        <slot name="result-content" />
      </div>
    </div>
  </div>
</template>

<script setup>
import BaseCardLikeButtons from './BaseCardLikeButtons.vue';

const id = useId();

defineProps({
  result: {
    type: Object,
    default: () => ({}),
  },
  isContentLoading: {
    type: Boolean,
    default: false,
  },
});

const emits = defineEmits(['openFeedbackModal', 'result-clicked']);

const openFeedbackModal = () => {
  emits('openFeedbackModal');
};

const handleCardClick = () => {
  emits('result-clicked');
};
</script>

<style lang="scss" scoped>
@use '../../global/scss/mixins/_breakpoints.scss' as bp;
@use '../../global/scss/mixins/_text.scss';

.base-card {
  --color-grey-75: #e9e9e9;
  --color-grey-100: #e0e0e0;
  --color-gray-200: #d3d3d3;
  --color-grey-500: #949494;
  --color-grey-950: #2d2d2d;
  --color-blue-500: #1850c1;
  --color-blue-50: #f3f6fc;
  --color-text-primary: #2d2d2d;
  --color-text-secondary: #474747;
  --color-text-tertiary: var(--color-grey-500);
  --color-purple-500: #662e80;
  --border-color: var(--color-grey-100);

  border-radius: 4px;
  border: 1px solid var(--border-color);
  padding: 16px;
  text-decoration: none;
  gap: 32px;

  position: relative;

  &:hover {
    box-shadow:
      0px 4px 6px -1px rgba(0, 0, 0, 0.1),
      0px 0px 5px 0px rgba(0, 0, 0, 0.12);
    cursor: pointer;
  }

  &:has(:focus-visible) {
    border: var(--color-purple-500) solid 1px;
    outline: var(--color-blue-500) solid 1px;
    outline-offset: 2px;
    border-radius: 8px;
  }

  a,
  button {
    &:focus-visible {
      outline: var(--color-blue-500) solid 1px;
      outline-offset: 2px;
    }
  }

  .row-1 {
    display: flex;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .row-2 {
    display: flex;
    gap: 16px;
  }

  .image-preview {
    display: flex;
    align-items: flex-start;
  }

  .content-section {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 12px;
  }

  .likes-section {
    color: var(--color-grey-500);
    display: flex;
    gap: 12px;
  }

  :deep(.preview-image),
  :deep(.preview-fallback) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 113px;
    aspect-ratio: 4/3;
    border-radius: 4px;
    background-color: var(--background-color);
    color: var(--text-color);

    img {
      width: 100%;
      aspect-ratio: inherit;
      object-fit: cover;
      border-radius: inherit;
    }

    @include bp.screen('<=sm') {
      width: 96px;
      height: 72px;
      aspect-ratio: 4/3;
    }
  }

  :deep(.title) {
    color: var(--color-text-primary);
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    letter-spacing: 0.8px;
    text-decoration: none;
    overflow-wrap: anywhere;
    line-height: 1.2;
    @include text.line-clamp(2);
  }

  :deep(.snippet) {
    color: var(--color-text-secondary);
    font-size: 14px;
    font-weight: 400;
    letter-spacing: 0.5px;
    word-break: break-word;
    line-height: 1.3;
    @include text.line-clamp(2);
  }

  :deep(.meta-info) {
    display: flex;
    gap: 24px;
    color: var(--color-text-secondary);
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    letter-spacing: 0.1px;
    .date,
    .location {
      display: flex;
      align-items: center;
      gap: 8px;
      text-decoration: none;
      color: var(--color-text-secondary);
      font-size: 12px;
      font-weight: 400;
      line-height: 19px;
      letter-spacing: 0.5px;
    }
    span {
      color: var(--color-text-primary);
    }
  }

  .tags {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 450;
    line-height: 16px;
    letter-spacing: 0.5px;
    text-decoration: none;
    .tag {
      display: inline-flex;
      padding: 4px 8px;
      justify-content: center;
      align-items: center;
      border-radius: 4px;
      background: var(--color-grey-75);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      text-decoration: none;
      cursor: default;
      color: var(--color-text-secondary);
      @include bp.screen('<=sm') {
        padding: 2px 7px;
      }
      &.tag-link:hover {
        cursor: pointer;
        background: var(--color-grey-100);
      }
    }
  }
}

@include bp.screen('<=sm') {
  .base-card {
    gap: 16px;
  }

  .tag {
    padding: 2px 7px;
  }
}
</style>
